// PO7-S4-F1 (docs/build-briefs/PO7-S4-F1-invoice-line-kinds.md, DEC-299). Pure
// helpers for the invoice review's line kinds: the fixed vocabulary in words
// (tile titles and sentences, the plus or minus chip), the Estimated versus
// final table, the "lines add to / the invoice says" check, and the confirm
// step's text. No DOM, no network. Nothing here decides a kind, a sign or a
// total: the server derived them and this file only says them. The vocabulary
// is picked from tiles and never typed.

import type {
  InvoiceConfirmedDto,
  InvoiceKindTotalDto,
  InvoiceNonProductKind,
  InvoiceReviewDto,
  InvoiceReviewLineDto,
} from "./api.ts";
import { formatDraftMoney } from "./invoiceDraftDisplay.ts";

export interface KindWords {
  kind: InvoiceNonProductKind;
  /** The tile title and the chip text. */
  label: string;
  /** The row label in Estimated versus final. */
  rowLabel: string;
  /** The tile's plain sentence, ending in what it does to the total. */
  sentence: string;
  /** Whether the kind adds to the invoice total or takes money off. */
  sign: "+" | "-";
}

/** The tile order of the approved mockup (po7-s4-f1-1-picker). */
export const KIND_TILES: readonly KindWords[] = [
  {
    kind: "shipping",
    label: "Shipping or delivery",
    rowLabel: "Shipping or delivery",
    sentence: "Freight and delivery charges. Adds to the total.",
    sign: "+",
  },
  {
    kind: "fee",
    label: "Fee",
    rowLabel: "Fees",
    sentence: "Fuel surcharge, handling, bag or keg fee. Adds to the total.",
    sign: "+",
  },
  {
    kind: "tax",
    label: "Tax",
    rowLabel: "Tax",
    sentence: "Sales or other tax printed on the invoice. Adds to the total.",
    sign: "+",
  },
  {
    kind: "deposit_charged",
    label: "Deposit charged",
    rowLabel: "Deposits charged",
    sentence: "Refundable deposit, such as a keg. Adds to the total.",
    sign: "+",
  },
  {
    kind: "credit",
    label: "Credit",
    rowLabel: "Credits",
    sentence: "Money off: damage, short ship, promo. Takes off the total.",
    sign: "-",
  },
  {
    kind: "return",
    label: "Return",
    rowLabel: "Returns",
    sentence: "Goods sent back for credit. Takes off the total.",
    sign: "-",
  },
  {
    kind: "deposit_returned",
    label: "Deposit returned",
    rowLabel: "Deposits returned",
    sentence: "Deposit given back when empties go back. Takes off the total.",
    sign: "-",
  },
];

/** The row order of the Estimated versus final table (po7-s4-f1-2-classified). */
export const KIND_ROW_ORDER: readonly InvoiceNonProductKind[] = [
  "shipping",
  "fee",
  "tax",
  "deposit_charged",
  "deposit_returned",
  "credit",
  "return",
];

export const NOT_PART_OF_COST = "Not part of item cost. Stock is not changed.";

export function kindWords(kind: InvoiceNonProductKind): KindWords {
  const found = KIND_TILES.find((t) => t.kind === kind);
  if (found === undefined) {
    throw new Error(`unknown invoice line kind: ${kind}`);
  }
  return found;
}

/** `+ Shipping or delivery`, `- Credit`. */
export function describeKindChip(kind: InvoiceNonProductKind): string {
  const words = kindWords(kind);
  return `${words.sign} ${words.label}`;
}

/** A counted line's signed amount: `$15.00`, `-$30.00`. */
export function formatEffect(cents: number): string {
  return formatDraftMoney(cents);
}

/** A change column figure: `+$78.30`, `-$30.00`, `$0.00`. */
export function formatChange(cents: number): string {
  if (cents === 0) {
    return formatDraftMoney(0);
  }
  return `${cents > 0 ? "+" : "-"}${formatDraftMoney(Math.abs(cents))}`;
}

/** The sheet's flag for a line the reader saw as a negative amount, since the
 * kind (not the printed sign) decides add or take off. Null when it printed
 * positive or unknown. */
export function describePrintedNegative(
  line: Pick<InvoiceReviewLineDto, "printedAmountCents">,
): string | null {
  const printed = line.printedAmountCents;
  if (printed === null || printed >= 0) {
    return null;
  }
  return `Printed as ${formatDraftMoney(printed)}. Credit, Return and Deposit returned take money off, the other kinds add.`;
}

/** The sheet's second line: what was read, and how to fix it. */
export function describeAmountRead(
  line: Pick<InvoiceReviewLineDto, "amountCents" | "printedAmountCents">,
): string {
  const cents = line.printedAmountCents;
  if (cents === null) {
    return "amount could not be read from the invoice";
  }
  return `amount read from the invoice: ${formatDraftMoney(Math.abs(cents))}`;
}

export interface TableRow {
  key: string;
  label: string;
  estimate: string;
  invoice: string;
  change: string;
  changesCost: "Yes" | "No" | "";
  tone: "normal" | "minus" | "notcounted" | "total";
}

export interface TableInput {
  estimateCents: number;
  productCents: number | null;
  byKind: readonly InvoiceKindTotalDto[];
  /** Left-out lines plus held-back lines: counted nowhere. */
  notCountedCount: number;
  notCountedCents: number;
  totalCents: number | null;
}

/** The Estimated versus final table (po7-s4-f1-2 and -3). The order estimate has
 * products only, so every other line shows as a change. A row for every kind
 * appears once any other line exists, so nothing is hidden, and a Left out row
 * appears whenever a line is left out or held back. */
export function buildEstimatedVersusFinal(input: TableInput): TableRow[] {
  const rows: TableRow[] = [];
  const product = input.productCents;
  rows.push({
    key: "products",
    label: "Products (matched)",
    estimate: formatDraftMoney(input.estimateCents),
    invoice: product === null ? "Not applied" : formatDraftMoney(product),
    change: product === null ? "" : formatChange(product - input.estimateCents),
    changesCost: "Yes",
    tone: "normal",
  });
  const anyOther = input.byKind.some((k) => k.count > 0) || input.notCountedCount > 0;
  if (anyOther) {
    for (const kind of KIND_ROW_ORDER) {
      const entry = input.byKind.find((k) => k.kind === kind);
      const effect = entry?.effectCents ?? 0;
      rows.push({
        key: kind,
        label: kindWords(kind).rowLabel,
        estimate: "None",
        invoice: formatDraftMoney(effect),
        change: formatChange(effect),
        changesCost: "No",
        tone: effect < 0 ? "minus" : "normal",
      });
    }
  }
  if (input.notCountedCount > 0) {
    rows.push({
      key: "left-out",
      label: "Left out, not counted",
      estimate: "None",
      invoice: formatDraftMoney(input.notCountedCents),
      change: "",
      changesCost: "No",
      tone: "notcounted",
    });
  }
  rows.push({
    key: "total",
    label: "Invoice total (added up from the lines)",
    estimate: formatDraftMoney(input.estimateCents),
    invoice: input.totalCents === null ? "Not applied" : formatDraftMoney(input.totalCents),
    change: input.totalCents === null ? "" : formatChange(input.totalCents - input.estimateCents),
    changesCost: "",
    tone: "total",
  });
  return rows;
}

export function tableInputFromReview(review: InvoiceReviewDto): TableInput {
  return {
    estimateCents: review.totals.poEstimatedCents,
    productCents: review.totals.invoiceMatchedCents,
    byKind: review.totals.byKind,
    notCountedCount: review.totals.leftOutCount + review.counts.heldBack,
    notCountedCents: review.totals.leftOutCents + review.totals.heldBackCents,
    totalCents: review.totals.invoiceTotalCents,
  };
}

export function tableInputFromConfirmed(confirmed: InvoiceConfirmedDto): TableInput {
  return {
    estimateCents: confirmed.estimatedTotalCents,
    productCents: confirmed.productTotalCents,
    byKind: confirmed.byKind,
    notCountedCount: confirmed.leftOutCount,
    notCountedCents: confirmed.leftOutCents,
    totalCents: confirmed.invoiceTotalCents,
  };
}

export interface PrintedCheck {
  tone: "ok" | "warn" | "unknown";
  title: string;
  detail: string;
  /** The signed difference (printed minus lines); null when unknown. */
  differenceCents: number | null;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? `1 ${one}` : `${count} ${many}`;
}

/** "Lines add to X. The invoice says Y." with the difference. Green at zero,
 * amber otherwise, neutral when the invoice's own total was not read. The
 * difference never blocks (DEC-299). */
export function describePrintedCheck(input: {
  linesCents: number | null;
  printedCents: number | null;
  differenceCents: number | null;
  leftOutCount: number;
  leftOutCents: number;
}): PrintedCheck {
  if (input.linesCents === null) {
    return {
      tone: "unknown",
      title: "Lines are not added up yet.",
      detail: "This invoice repeats another one, so nothing applies to the total.",
      differenceCents: null,
    };
  }
  if (input.printedCents === null || input.differenceCents === null) {
    return {
      tone: "unknown",
      title: `Lines add to ${formatDraftMoney(input.linesCents)}.`,
      detail: "No total was read from the invoice, so there is nothing to compare with.",
      differenceCents: null,
    };
  }
  const title = `Lines add to ${formatDraftMoney(input.linesCents)}. The invoice says ${formatDraftMoney(input.printedCents)}.`;
  if (input.differenceCents === 0) {
    return {
      tone: "ok",
      title,
      detail: "Difference $0.00. Nothing is missing.",
      differenceCents: 0,
    };
  }
  const size = formatDraftMoney(Math.abs(input.differenceCents));
  const left =
    input.leftOutCount === 0
      ? ""
      : input.leftOutCount === 1 && input.leftOutCents === Math.abs(input.differenceCents)
        ? ` One line is left out, and it is ${size}.`
        : ` ${plural(input.leftOutCount, "line is", "lines are")} left out, ${formatDraftMoney(input.leftOutCents)} in all.`;
  return {
    tone: "warn",
    title,
    detail: `Difference ${size}.${left}`,
    differenceCents: input.differenceCents,
  };
}

export function printedCheckFromReview(review: InvoiceReviewDto): PrintedCheck {
  return describePrintedCheck({
    linesCents: review.totals.invoiceTotalCents,
    printedCents: review.totals.printedTotalCents,
    differenceCents: review.totals.printedDifferenceCents,
    leftOutCount: review.totals.leftOutCount,
    leftOutCents: review.totals.leftOutCents,
  });
}

export function printedCheckFromConfirmed(confirmed: InvoiceConfirmedDto): PrintedCheck {
  return describePrintedCheck({
    linesCents: confirmed.invoiceTotalCents,
    printedCents: confirmed.printedTotalCents,
    differenceCents: confirmed.printedDifferenceCents,
    leftOutCount: confirmed.leftOutCount,
    leftOutCents: confirmed.leftOutCents,
  });
}

/** The confirm step's words. A printed-total difference is named, and the button
 * says so; with none the step is as S4 built it. */
export function describeConfirmStep(review: InvoiceReviewDto): {
  title: string;
  body: string;
  button: string;
  hasDifference: boolean;
} {
  const number = review.invoiceNumber === null ? "" : ` #${review.invoiceNumber}`;
  const difference = review.totals.printedDifferenceCents;
  if (difference !== null && difference !== 0) {
    const check = printedCheckFromReview(review);
    return {
      title: `Confirm invoice${number} with a ${formatDraftMoney(Math.abs(difference))} difference?`,
      body:
        `${check.title} The difference is saved with the invoice. ` +
        "Item cost changes for products only. How many are on hand does not change.",
      button: "Confirm with the difference",
      hasDifference: true,
    };
  }
  return {
    title: `Confirm invoice${number}?`,
    body:
      "The cost of the items you received will change to what the vendor billed. " +
      "How many you have on hand does not change.",
    button: "Confirm",
    hasDifference: false,
  };
}

/** `Of the +$49.02: products +$78.30, shipping +$15.00, ...` for the Confirmed panel. */
export function describeSplitOfChange(confirmed: InvoiceConfirmedDto): string | null {
  if (confirmed.byKind.every((k) => k.count === 0)) {
    return null;
  }
  const effect = (kind: InvoiceNonProductKind): number =>
    confirmed.byKind.find((k) => k.kind === kind)?.effectCents ?? 0;
  const products = confirmed.productTotalCents - confirmed.estimatedTotalCents;
  const deposits = effect("deposit_charged") + effect("deposit_returned");
  const total = confirmed.invoiceTotalCents - confirmed.estimatedTotalCents;
  return (
    `Of the ${formatChange(total)}: products ${formatChange(products)}, ` +
    `shipping ${formatChange(effect("shipping"))}, fees ${formatChange(effect("fee"))}, ` +
    `tax ${formatChange(effect("tax"))}, deposits ${formatChange(deposits)} net, ` +
    `credits ${formatChange(effect("credit"))}, returns ${formatChange(effect("return"))}.`
  );
}

/** The Other lines heading's count. */
export function describeOtherCount(counted: number, leftOut: number): string {
  const all = counted + leftOut;
  if (leftOut === 0) {
    return `${plural(all, "line", "lines")}, ${all === 1 ? "counted in the total" : "each one counted in the total"}`;
  }
  return `${plural(all, "line", "lines")}, ${counted} counted, ${leftOut} left out`;
}

export function describeLeftOutLine(amountCents: number | null): string {
  return amountCents === null
    ? "Left out by you. Counted nowhere."
    : `Left out by you. Counted nowhere. ${formatDraftMoney(amountCents)} on the invoice.`;
}
